import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { money } from '@/components/staffAttendance/PayrollAgencyCard';

const STEP = 100;
const HEAD = ['Staff ID', 'Name', 'Section', 'Pay type', 'Fixed / month', 'Worked days', 'Fixed days', 'Night hrs', 'Fixed amount', 'Piece-rate', 'Supervisor', 'Flags', 'Pay'];

const PayBadge = ({ r }) => (r.pay_type === 'fixed'
  ? <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">Fixed</Badge>
  : <div className="space-y-0.5"><Badge className="bg-teal-50 text-teal-700 ring-1 ring-teal-200 hover:bg-teal-50">Variable</Badge>
    {!r.pay_type_set && <div className="text-[10px] font-semibold text-rose-600" data-testid={`regpay-notset-${r.staff_id}`}>Pay type not set</div>}</div>);

const Flags = ({ r }) => (
  <div className="flex flex-wrap gap-1 text-[11px] font-semibold">
    {r.review > 0 && <span className="rounded bg-amber-100 px-1.5 text-amber-800" data-testid={`regpay-flag-review-${r.staff_id}`}>{r.review} HR?</span>}
    {r.missed > 0 && <span className="rounded bg-rose-100 px-1.5 text-rose-700" data-testid={`regpay-flag-missed-${r.staff_id}`}>{r.missed} M</span>}
    {r.pending > 0 && <span className="rounded bg-sky-100 px-1.5 text-sky-800" data-testid={`regpay-flag-pending-${r.staff_id}`}>{r.pending} wait</span>}
    {r.no_amount && <span className="rounded bg-rose-100 px-1.5 text-rose-700">No amount</span>}
  </div>
);

const Var = ({ r, v, k }) => (
  <TableCell className={`tabular-nums ${r.pay_type === 'fixed' ? 'text-gray-400 line-through' : ''}`} title={r.pay_type === 'fixed' ? 'Not paid — Fixed pay only' : undefined}
    data-testid={`regpay-${k}-${r.staff_id}`}>{v ? money(v) : '—'}</TableCell>
);

const Row = ({ r }) => {
  const fx = r.pay_type === 'fixed';
  return (
    <TableRow data-testid={`regpay-row-${r.staff_id}`} className={r.roll_status === 'left' ? 'text-gray-500' : ''}>
      <TableCell className="font-mono">{r.staff_id}</TableCell>
      <TableCell className="font-medium">{r.staff_name}{r.roll_status === 'left' && <Badge className="ml-2 bg-rose-100 text-rose-700 hover:bg-rose-100">Left</Badge>}</TableCell>
      <TableCell className="text-xs">{r.section || '—'}</TableCell>
      <TableCell data-testid={`regpay-type-${r.staff_id}`}><PayBadge r={r} /></TableCell>
      <TableCell className="tabular-nums text-xs">{fx ? <>{money(r.fixed_pay)}<div className="text-gray-500">{money(r.per_day)} / day</div></> : '—'}</TableCell>
      <TableCell className="tabular-nums">{r.worked_days}</TableCell>
      <TableCell className="tabular-nums font-semibold" data-testid={`regpay-days-${r.staff_id}`}>{fx ? r.days : '—'}</TableCell>
      <TableCell className="tabular-nums" data-testid={`regpay-night-${r.staff_id}`}>{fx ? r.night_hours : '—'}</TableCell>
      <TableCell className="tabular-nums text-xs" data-testid={`regpay-fixed-${r.staff_id}`}>
        {fx ? <>{money(r.fixed_amount)}{r.night_amount > 0 && <div className="text-gray-500">+ night {money(r.night_amount)}</div>}</> : '—'}
      </TableCell>
      <Var r={r} v={r.piece_amount} k="piece" />
      <Var r={r} v={r.supervisor_amount} k="sup" />
      <TableCell><Flags r={r} /></TableCell>
      <TableCell className="font-bold tabular-nums" data-testid={`regpay-amount-${r.staff_id}`}>{money(r.amount)}</TableCell>
    </TableRow>
  );
};

/** Staff-wise regular payroll: Fixed (÷ 26 × days + night hrs) or Variable (piece-rate + supervisor). */
export const RegularPayrollTable = ({ rows }) => {
  const [limit, setLimit] = useState(STEP);
  return (
    <Card className="border-0 shadow-md overflow-hidden" data-testid="regpay-table">
      <CardContent className="p-0 overflow-x-auto">
        <Table>
          <TableHeader><TableRow>{HEAD.map((h) => <TableHead key={h}>{h}</TableHead>)}</TableRow></TableHeader>
          <TableBody>{rows.slice(0, limit).map((r) => <Row key={r.staff_id} r={r} />)}</TableBody>
        </Table>
        {rows.length > limit && (
          <Button variant="ghost" className="w-full rounded-none border-t text-[#2D5F4F]" onClick={() => setLimit(limit + STEP)} data-testid="regpay-show-more">
            Show more ({rows.length - limit} more staff)
          </Button>
        )}
      </CardContent>
    </Card>
  );
};

export default RegularPayrollTable;
